import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { MessageCircleOff } from "lucide-react";
import { useSelector } from "react-redux";

import Card from "../../../../Card";
import DocumentsSection from "../../../../Company/CompanyViewDetails/Documents/DocumentsSection";
import {
  UIAvatar,
  UIButton,
  UIEmptyState,
  UIExpandableImage,
  UIPageHeader,
  UITextarea,
  getUserInitials,
} from "../../../../ui";
import messages from "../../../../../assets/locale/messages";
import { clx } from "../../../../../utils/Helpers";
import { REPORT_COMMENT_MAX_LENGTH } from "../reportUpload";

import "./ReportView.scss";

const COMMENTS_EMPTY_CLASS_NAMES = {
  root: "ui-empty-state--embedded report-view__comments-empty",
  title: "report-view__comments-empty-title",
  description: "report-view__comments-empty-description",
  iconCircle: "report-view__comments-empty-icon",
};

const ReportView = ({
  report,
  labels,
  canResolve = false,
  canComment = false,
  commenting = false,
  onResolve,
  onPostComment,
  onViewProfile,
  className,
}) => {
  const [commentText, setCommentText] = useState("");
  const lang = useSelector((state) => state.locale.lang);
  const { shared } = messages[lang];

  if (!report) return null;

  const showViewProfile = Boolean(onViewProfile);

  const imageMedia = report.media.filter((item) => item.isImage);
  const fileMedia = report.media.filter((item) => !item.isImage);
  const fileDocuments = fileMedia.map((item, index) => {
    const fileName = item.fileName || item.name || `file-${index + 1}`;
    const label =
      item.name && item.name !== fileName
        ? item.name
        : `${labels.evidenceMedia} ${index + 1}`;

    return {
      rowKey: item.id || `${item.url}-${index}`,
      name: label,
      fileName,
      url: item.url,
    };
  });

  const handlePostComment = () => {
    const next = commentText.trim();
    if (!next || !onPostComment) return;
    onPostComment(next);
    setCommentText("");
  };

  const commentForm = canComment && report.isOpen ? (
    <Box className="report-view__comment-form">
      <UITextarea
        name="reportComment"
        value={commentText}
        onChange={(event) => setCommentText(event?.target?.value ?? "")}
        placeholder={labels.addCommentPlaceholder}
        minRows={3}
        maxLength={REPORT_COMMENT_MAX_LENGTH}
        maxLengthMessage={labels.commentMaxLengthError}
      />
      <div className="report-view__comment-form-actions">
        <UIButton
          type="button"
          variant="primary"
          appearance="outline"
          size="medium"
          disabled={
            !commentText.trim() ||
            commentText.length > REPORT_COMMENT_MAX_LENGTH ||
            commenting
          }
          onClick={handlePostComment}
        >
          {labels.postComment}
        </UIButton>
      </div>
    </Box>
  ) : null;

  const status = report.status === "closed" ? "closed" : "open";
  const subtitle =
    report.priority !== "-"
      ? `${labels.priority}: ${report.priority}`
      : null;
  const resolveAction =
    canResolve && report.isOpen && onResolve ? (
      <UIButton
        type="button"
        variant="success"
        size="medium"
        onClick={onResolve}
      >
        {labels.resolveIssue}
      </UIButton>
    ) : null;

  return (
    <div className={clx("report-view", className)}>
      <UIPageHeader
        className="report-view__page-header"
        title={report.title}
        status={status}
        statusLabel={report.statusLabel}
        icon={
          status === "closed" ? (
            <ShieldOutlinedIcon />
          ) : (
            <ErrorOutlineIcon />
          )
        }
        iconStatus={status}
        subtitle={subtitle}
        actions={resolveAction}
      />

      <div className="report-view__layout">
        <div className="report-view__main">
          <Card title={labels.metadata}>
            <div className="report-view__meta-grid">
              <div className="event-detail-field">
                <span className="event-detail-label">{labels.zone}</span>
                <span className="event-detail-value">{report.zoneName}</span>
                {report.zoneSubLabel !== "-" ? (
                  <span className="event-detail-value event-detail-value--muted">
                    {report.zoneSubLabel}
                  </span>
                ) : null}
              </div>
              <div className="event-detail-field">
                <span className="event-detail-label">{labels.reportedAt}</span>
                <span className="event-detail-value">{report.reportedAt}</span>
              </div>
              <div className="event-detail-field">
                <span className="event-detail-label">{labels.resolvedAt}</span>
                <span className="event-detail-value">{report.resolvedAt}</span>
              </div>
              <div className="event-detail-field">
                <span className="event-detail-label">{labels.durationOpen}</span>
                <span className="event-detail-value event-detail-value--accent">
                  {report.durationOpen}
                </span>
              </div>
            </div>
          </Card>

          <Card title={labels.issueDescription}>
            <div
              className="event-detail-value-box report-view__description"
              dir="auto"
            >
              {report.description}
            </div>
          </Card>

          <Card
            title={labels.evidenceMedia}
            subtitle={labels.filesAttached.replace(
              "{count}",
              report.mediaCount,
            )}
          >
            {report.mediaCount > 0 ? (
              <div className="report-view__media">
                {imageMedia.length > 0 ? (
                  <div className="report-view__media-grid">
                    {imageMedia.map((item, index) => (
                      <div
                        className="report-view__media-item"
                        key={item.id || `${item.url}-${index}`}
                      >
                        <UIExpandableImage
                          src={item.url}
                          alt={
                            item.name ||
                            `${labels.evidenceMedia} ${index + 1}`
                          }
                          frameSx={{
                            width: "100%",
                            height: "100%",
                            display: "block",
                          }}
                          thumbnailImgSx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                          expandAriaLabel={shared.expandImageFullSize}
                          closeAriaLabel={shared.closeImageViewer}
                        />
                      </div>
                    ))}
                  </div>
                ) : null}

                {fileDocuments.length > 0 ? (
                  <DocumentsSection
                    asCard={false}
                    stacked
                    showPreview={false}
                    documents={fileDocuments}
                    className="report-view__documents"
                  />
                ) : null}
              </div>
            ) : (
              <span className="event-detail-value event-detail-value--muted">
                {labels.noPreview}
              </span>
            )}
          </Card>
        </div>

        <aside className="report-view__aside">
          <Card title={labels.reportingBy}>
            <div className="report-view__reporter">
              <UIAvatar
                name={report.reporterName}
                src={report.reporterImage || undefined}
                size="xl"
                className="report-view__reporter-avatar"
              />
              <div className="report-view__reporter-text">
                <span className="report-view__reporter-name">
                  {report.reporterName}
                </span>
                <span className="report-view__reporter-role">
                  {report.reporterRole}
                </span>
              </div>
              {showViewProfile ? (
                <UIButton
                  type="button"
                  variant="outline"
                  size="medium"
                  className="report-view__view-profile"
                  onClick={() => onViewProfile?.()}
                >
                  {labels.viewProfile}
                </UIButton>
              ) : null}
            </div>
          </Card>

          <Card
            title={labels.comments}
            classNames={{ root: "report-view__comments" }}
          >
            <div className="report-view__comments-list">
              {report.comments.length > 0 ? (
                report.comments.map((comment) => (
                  <div
                    className="report-view__comment"
                    key={comment.id || comment.text}
                  >
                    <UIAvatar
                      name={comment.name}
                      src={comment.image || undefined}
                      size="sm"
                      className="report-view__comment-avatar"
                      initials={
                        comment.initials || getUserInitials(comment.name)
                      }
                    />
                    <div className="report-view__comment-meta">
                      <span className="report-view__comment-name">
                        {comment.name}
                      </span>
                      <span className="report-view__comment-datetime">
                        {comment.date}
                        {comment.time !== "-" ? ` • ${comment.time}` : ""}
                      </span>
                    </div>
                    <div className="report-view__comment-bubble" dir="auto">
                      {comment.text}
                    </div>
                  </div>
                ))
              ) : (
                <UIEmptyState
                  title={labels.emptyCommentsTitle}
                  description={labels.emptyCommentsDescription}
                  icon={<MessageCircleOff size={40} strokeWidth={1.5} />}
                  classNames={COMMENTS_EMPTY_CLASS_NAMES}
                />
              )}
            </div>
            {commentForm}
          </Card>
        </aside>
      </div>
    </div>
  );
};

ReportView.propTypes = {
  report: PropTypes.object,
  labels: PropTypes.object.isRequired,
  canResolve: PropTypes.bool,
  canComment: PropTypes.bool,
  commenting: PropTypes.bool,
  onResolve: PropTypes.func,
  onPostComment: PropTypes.func,
  onViewProfile: PropTypes.func,
  className: PropTypes.string,
};

ReportView.defaultProps = {
  report: null,
  canResolve: false,
  canComment: false,
  commenting: false,
  onResolve: undefined,
  onPostComment: undefined,
  onViewProfile: undefined,
  className: "",
};

export default ReportView;
